import { useUpdateUserProfile } from 'hooks/use-set-user-profile/use-set-user-profile.hook';
import { PHONE_NUMBER_TYPES } from 'common/constants/account-info';
import { useUpdateUserEmail } from 'hooks/use-update-user-email/use-update-user-email.hook';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { useSelector } from '@xstate/react';

export type AccountSetupInputs = {
  mobilePhoneNumber?: string;
  homePhoneNumber?: string;
  alternatePhoneNumber?: string;
  email?: string;
  isTextNotificationsEnabled?: boolean;
};

export const useUpdateAccountSetup = () => {
  const { updateUserProfile } = useUpdateUserProfile();
  const updateUserEmailMutation = useUpdateUserEmail();

  const actor = OnboardingMachineContext.useActorRef();
  const onboardingMachineSnapshotContext = useSelector(
    actor,
    (machineSnapshot) => machineSnapshot.context,
  );

  const updateAccountSetup = async (values: AccountSetupInputs) => {
    const {
      mobilePhoneNumber,
      homePhoneNumber,
      email,
      isTextNotificationsEnabled,
    } = values;

    const phoneNumbers = [];

    if (
      mobilePhoneNumber ||
      onboardingMachineSnapshotContext.mobilePhoneNumber
    ) {
      phoneNumbers.push({
        description: PHONE_NUMBER_TYPES.MOBILE,
        number:
          mobilePhoneNumber ||
          onboardingMachineSnapshotContext.mobilePhoneNumber,
      });
    }

    if (homePhoneNumber || onboardingMachineSnapshotContext.homePhoneNumber) {
      phoneNumbers.push({
        description: PHONE_NUMBER_TYPES.HOME,
        number:
          homePhoneNumber || onboardingMachineSnapshotContext.homePhoneNumber,
      });
    }

    const { alternatePhoneNumber } = onboardingMachineSnapshotContext;
    if (alternatePhoneNumber) {
      phoneNumbers.push({
        description: PHONE_NUMBER_TYPES.ALTERNATE,
        number: alternatePhoneNumber,
      });
    }

    let updatedUserProfileData = { phoneNumbers };

    if (isTextNotificationsEnabled !== undefined) {
      updatedUserProfileData = Object.assign(updatedUserProfileData, {
        notificationPreferences: {
          disableSms: !isTextNotificationsEnabled,
        },
      });
    }

    if (email) {
      updatedUserProfileData = Object.assign(updatedUserProfileData, {
        email,
      });

      await updateUserEmailMutation.mutateAsync({
        emailAddress: email,
      });
    }

    return updateUserProfile(updatedUserProfileData);
  };

  return {
    updateAccountSetup,
  };
};
